import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import clsx from 'clsx';
import { useCallback, useMemo } from 'react';

import { TableCellCheckbox } from '~components/generic/table/TableCellCheckbox';
import { TableCellHeader } from '~components/generic/table/TableCellHeader';
import { TableCellHeaderCheckbox } from '~components/generic/table/TableCellHeaderCheckbox';
import { TableCellName } from '~components/generic/table/TableCellName';
import { updateItemInArrayAtPosition } from '~services/immutable';
import styles from '~components/generic/table/Table.module.css';

export type TableDataItemShape = {
  uid: number;
  username: string;
  mail: string;
  fullname: string;
  selected?: boolean;
};
export type TableDataShape = TableDataItemShape[];

export type TableProps = {
  data: TableDataShape;
  onUpdateData: (data: TableDataShape) => void;
  disabled: boolean;
};

export const AddUsersTable = ({ data, onUpdateData, disabled }: TableProps) => {
  const columns = [
    {
      header: ({ column }) => (
        <TableCellHeaderCheckbox
          updateData={() => {
            updateSelected(undefined, !allItemsSelected);
          }}
          column={column}
          disabled={disabled}
          allItemsSelected={allItemsSelected}
        />
      ),
      accessorKey: 'selected',
      cell: ({ row, column }) => (
        <TableCellCheckbox
          value={row.original?.selected ?? false}
          updateData={() => {
            console.log('updateData');
            updateSelected(row.index, !row.original?.selected);
          }}
          row={row}
          column={column}
          disabled={disabled}
        />
      ),
    },
    {
      header: () => <TableCellHeader header="tableHeaderUserUid" />,
      accessorKey: 'uid',
      cell: ({ getValue, row }) => <TableCellName value={getValue()} row={row} />,
    },
    {
      header: () => <TableCellHeader header="tableHeaderUserUsername" />,
      accessorKey: 'username',
      cell: ({ getValue, row }) => <TableCellName value={getValue()} row={row} />,
    },
    {
      header: () => <TableCellHeader header="tableHeaderUserFullname" />,
      accessorKey: 'fullname',
      cell: ({ getValue, row }) => <TableCellName value={getValue()} row={row} />,
    },
    {
      header: () => <TableCellHeader header="tableHeaderUserMail" />,
      accessorKey: 'mail',
      cell: ({ getValue, row }) => <TableCellName value={getValue()} row={row} />,
    },
  ];

  /*
  Optional TODO: reactivate if required. 

  const DefaultKeys = DefaultColumns.map(col => col.accessor);

  // Define: prepare columns for table
  const columns = useMemo(() => {
    const cols = [...DefaultColumns];
    if (data.length > 0) {
      Object.keys(data[0])
        .filter(key => !DefaultKeys.includes(key))
        .forEach(key => {
          const keyName = toPascalCase(key);
          cols.push({
            Header: `tableHeaderUser${keyName}`,
            HeaderFallback: keyName,
            accessor: key,
          });
        });
    }
    return cols;
  }, [data]);
  */

  // Handler: Toggle cell checkbox
  const updateSelected = useCallback(
    (rowIndex: number, value: boolean) => {
      if (!isNaN(rowIndex)) {
        onUpdateData(
          updateItemInArrayAtPosition(
            data,
            {
              selected: value,
            },
            rowIndex,
          ),
        );
      } else {
        onUpdateData(
          data.map(item => ({
            ...item,
            selected: value,
          })),
        );
      }
    },
    [data, onUpdateData],
  );

  const allItemsSelected = useMemo(() => !data.find(item => !item.selected), [data]);

  // Generate Table props
  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className={styles.container}>
      <table className={styles.table}>
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => {
                return (
                  <th key={header.id} className={clsx(styles.th, index === 0 && styles.thCheckbox)}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr key={row.id} className={styles.tr}>
              {row.getVisibleCells().map(cell => (
                <td key={cell.id} className={styles.td}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
